import { useReporting } from '@/hooks/useReporting';
import { initializeReporting, ProviderType } from '@/reporting';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getRouteCnName } from '@/utils/route-metadata';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

interface PendingAttributionError {
  error_name: string;
  error_message: string;
  url_path: string;
  query_string_length: number;
}

type AttributionErrorWindow = Window &
  typeof globalThis & {
    __pendingAttributionError?: PendingAttributionError;
  };

export const useEventReportingInit = ({ deviceType }: { deviceType: EPlatform }) => {
  const { trackEvent } = useReporting();
  const pathname = usePathname();
  const lastOpenAiPathnameRef = useRef<string | null>(null);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  // 从 store 中读取 BFF 下发的 tracking_config
  const trackingConfig = useStore((state) => state.publicResourceData?.jsonvalues?.tracking_config);
  // 从 store 中读取 SGTM 像素开关配置（服务端 10 分钟轮询注入）
  const sgtmPixelConfig = useStore((state) => state.sgtmPixelConfig);

  // 检查是否为特殊页面，需要排除 LandingPageView 追踪
  const shouldExcludeLandingPageView = () => {
    if (typeof window === 'undefined') return false;

    const { pathname: currentPath, search, host } = window.location;
    const { referrer } = document;

    // 检查特定路径
    const excludePaths = [
      '/paymentSuccess',
      '/PaymentFailed',
      '/waiting-response',
      '/price-difference-payment-failed',
      '/price-difference-payment-successful',
      '/waiting-response-pay-center',
      '/account/my-orders',
    ];

    /**
     * 打开app，style-hub 的 js sdk 不应上报，自定义着陆页事件 LandingPage_View
     * 由 app 上报 LandingPage_View
     * https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/request/57c4931a0550f4762fd94db3d0
     */
    if (inAppH5 && ['/style-hub'].includes(currentPath)) {
      return true;
    }

    if (excludePaths.includes(currentPath)) {
      return true;
    }

    // 检查是否包含 token 参数且不是内部 app
    if (/[?&]token=([^&]*)/.test(search) && !inAppH5) {
      return true;
    }

    // 检查是否为订单详情页
    if (currentPath.includes('/account/order-detail/')) {
      return true;
    }

    // 检查来源域名
    const allowedHosts = [host, 'zeelool.com', 'xmslol.cn'];
    if (allowedHosts.some((item) => referrer.includes(item))) {
      return true;
    }

    return false;
  };

  const SDLandingPageView = () => {
    // 检查是否需要排除 LandingPageView 追踪
    if (shouldExcludeLandingPageView()) {
      return;
    }

    const sdEventName = `LandingPage_View`;
    const sdEventParams = {
      landingpage_name: getRouteCnName(pathname),
      landingpage_url_path: pathname,
    };
    trackEvent(
      {
        eventName: sdEventName,
        properties: sdEventParams,
      },
      [ProviderType.SENSORS],
    );
  };
  useEffect(() => {
    // Initialize reporting when the app loads，传入 BFF 下发的 tracking_config 和 SGTM 像素开关
    initializeReporting(deviceType, trackingConfig, sgtmPixelConfig);

    const attributionErrorWindow = window as AttributionErrorWindow;
    const pendingAttributionError = attributionErrorWindow.__pendingAttributionError;
    if (pendingAttributionError) {
      trackEvent(
        {
          eventName: 'AttributionError_Monitor',
          properties: pendingAttributionError,
          options: { omitPixel: true },
        },
        [ProviderType.SNOWPLOW],
      );
      delete attributionErrorWindow.__pendingAttributionError;
    }

    SDLandingPageView();

    if (
      ![
        '/price-difference',
        '/price-difference-payment-successful',
        '/price-difference-payment-failed',
      ].includes(location.pathname)
    ) {
      trackEvent(
        {
          commandType: 'track',
          eventName: 'PageView',
        },
        [ProviderType.FACEBOOK],
      );
    }
  }, []);

  useEffect(() => {
    const routePath = pathname || '/';
    if (lastOpenAiPathnameRef.current === routePath) return;

    lastOpenAiPathnameRef.current = routePath;
    trackEvent(
      {
        eventName: 'page_viewed',
        properties: {
          type: 'contents',
          contents: [
            {
              id: routePath,
              name: getRouteCnName(routePath),
              content_type: 'page',
            },
          ],
        },
      },
      [ProviderType.OPENAI],
    );
  }, [pathname, trackEvent]);
};
